import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CredentialRevealService } from '../../services/credential-reveal.service';

/**
 * CredentialRevealComponent — the system "new user password" modal (Gmail-admin style).
 *
 * Mounted once at the app root (app.component.html) so it overlays both the editor and the runtime
 * artifact. Driven entirely by CredentialRevealService; UserService.addUserToTenant + create() open it.
 *
 *  confirm stage  — email ONLY + what will happen. Cancel / Create user.
 *  created stage  — the generated password, revealed once, + copy + Done.
 *
 * The password is deliberately NOT shown on the confirm stage. It used to be, and it made the
 * dialog read as "here is an existing credential" rather than "confirm to create" — admins hit
 * Cancel on a screen they didn't understand and the block returned 'cancelled' with nothing created.
 * Nothing exists until Create user is pressed, so there is nothing meaningful to show before then.
 */
@Component({
  selector: 'app-credential-reveal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
  <div *ngIf="svc.state | async as s"
       class="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
    <div class="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-gray-200 overflow-hidden">
      <div class="px-6 py-5">
        <h2 class="text-lg font-semibold text-gray-900">
          {{ s.stage === 'created' ? 'User added' : 'Add user' }}
        </h2>
        <p class="text-xs text-gray-500 mt-1">
          {{ s.stage === 'created'
              ? 'Copy the password below and share it with the user. An invite email has also been sent.'
              : 'This will create a new user and email them an invite to set their own password.' }}
        </p>

        <!-- Login id — shown at both stages -->
        <label class="block text-xs font-medium text-gray-600 mt-4 mb-1">Login (email)</label>
        <div class="px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-800 truncate">{{ s.email }}</div>

        <!-- Password — ONLY after the user actually exists -->
        <ng-container *ngIf="s.stage === 'created'">
          <label class="block text-xs font-medium text-gray-600 mt-3 mb-1">Temporary password</label>
          <div class="flex items-center gap-2">
            <input
              [ngModel]="s.password"
              readonly
              type="text"
              class="flex-1 px-3 py-2 rounded-lg border border-gray-300 bg-gray-50 text-sm font-mono text-gray-900 focus:outline-none"
            />
            <button type="button" (click)="svc.copy()"
                    class="px-2.5 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">
              {{ s.copied ? '✓' : 'Copy' }}
            </button>
          </div>

          <p class="mt-3 text-xs text-amber-600 flex items-start gap-1.5">
            <span>⚠️</span>
            <span>Copy this password now — for security it won't be shown again once you close this.</span>
          </p>
        </ng-container>

        <!-- Confirm stage — say plainly what Cancel does, so it isn't a guess -->
        <p *ngIf="s.stage === 'confirm'" class="mt-4 text-xs text-gray-500 flex items-start gap-1.5">
          <span>ℹ️</span>
          <span>Nothing is created until you press <strong class="text-gray-700">Create user</strong>.
                Cancel closes this and no user is added.</span>
        </p>

        <p *ngIf="s.error" class="mt-3 text-sm text-red-500">{{ s.error }}</p>
      </div>

      <!-- Actions -->
      <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
        <ng-container *ngIf="s.stage === 'confirm'">
          <button type="button" (click)="svc.cancel()" [disabled]="s.busy"
                  class="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50">Cancel</button>
          <button type="button" (click)="svc.continue()" [disabled]="s.busy"
                  class="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gray-900 hover:bg-gray-700 disabled:opacity-50">
            {{ s.busy ? 'Adding…' : 'Create user' }}
          </button>
        </ng-container>
        <button *ngIf="s.stage === 'created'" type="button" (click)="svc.close()"
                class="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gray-900 hover:bg-gray-700">Done</button>
      </div>
    </div>
  </div>
  `,
})
export class CredentialRevealComponent {
  constructor(public svc: CredentialRevealService) {}
}
